import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import { ProjectEnrollmentReportRow } from '../../types/content';
import { API_PATHS, apiHeaders, apiUrl } from '../../config/api';
import Button from '../../components/UI/Button';
import { confirmAction } from '../../utils/confirmAction';
import { formatDate } from '../../utils/formatDate';

const monthKey = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key: string) => {
  if (!key) return 'Unknown';
  const [year, month] = key.split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, 1);
  return date.toLocaleString(undefined, { month: 'short', year: 'numeric' });
};

const ProjectReportsAdminPage: React.FC = () => {
  const [rows, setRows] = useState<ProjectEnrollmentReportRow[]>([]);
  const [mobiles, setMobiles] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const load = async () => {
    try {
      const r = await contentApi.projectEnrollmentReport();
      const list = r.data.rows || [];
      setRows(list);
      const ids = [...new Set(list.map((item) => item.userProfileId).filter((id) => id > 0))];
      if (!ids.length) {
        setMobiles({});
        return;
      }
      const profilesResponse = await fetch(apiUrl(API_PATHS.userProfile.byIds), {
        method: 'POST',
        headers: { ...apiHeaders(localStorage.getItem('token')), 'Content-Type': 'application/json' },
        body: JSON.stringify({ userProfileIds: ids }),
      }).then((res) => res.json()).catch(() => null);
      const mobileMap: Record<number, string> = {};
      for (const profile of (profilesResponse?.userProfiles || []) as Array<{ id?: number; mobile?: string }>) {
        const id = Number(profile.id);
        const mobile = String(profile.mobile || '').trim();
        if (id > 0 && mobile) mobileMap[id] = mobile;
      }
      setMobiles(mobileMap);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the report.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const monthOptions = useMemo(() => {
    const keys = [...new Set(rows.map((row) => monthKey(row.dateCreated)).filter(Boolean))];
    return keys.sort((a, b) => b.localeCompare(a));
  }, [rows]);

  const projectOptions = useMemo(() => {
    const titles = [...new Set(rows.map((row) => row.projectTitle).filter(Boolean))];
    return titles.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const statusOptions = useMemo(() => {
    const values = [...new Set(rows.map((row) => row.status).filter(Boolean))];
    return values.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    if (monthFilter && monthKey(row.dateCreated) !== monthFilter) return false;
    if (projectFilter && row.projectTitle !== projectFilter) return false;
    if (statusFilter && row.status !== statusFilter) return false;
    return true;
  }), [rows, monthFilter, projectFilter, statusFilter]);

  const cancelRegistration = async (row: ProjectEnrollmentReportRow) => {
    if (/^cancelled$/i.test(row.status)) return;
    const ok = await confirmAction({
      title: 'Cancel registration?',
      description: `Cancel ${row.userEmail || `profile #${row.userProfileId}`} on “${row.projectTitle}”? Status becomes Cancelled.`,
      confirmLabel: 'Cancel registration',
      variant: 'danger',
    });
    if (!ok) return;
    setCancellingId(row.enrollmentId);
    try {
      const response = await contentApi.cancelProjectEnrollment(row.enrollmentId);
      if (!response.data.success) {
        setError(response.data.message || 'Could not cancel registration.');
        return;
      }
      setRows((prev) => prev.map((item) => (
        item.enrollmentId === row.enrollmentId ? { ...item, status: 'Cancelled' } : item
      )));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not cancel registration.');
    } finally {
      setCancellingId(null);
    }
  };

  if (error && rows.length === 0) return <p className="text-gray-600">{error}</p>;

  return (
    <div>
      <p className="text-gray-600 mb-4">Learners who started a self-paced project, including Git submissions. Owner and Course Admin can cancel a registration.</p>
      {error && <p className="mb-3 text-sm text-error-600">{error}</p>}

      <div className="mb-4 flex flex-wrap gap-3">
        <label className="text-sm text-gray-600">
          Month
          <select className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="">All months</option>
            {monthOptions.map((key) => <option key={key} value={key}>{monthLabel(key)}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Project
          <select className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
            <option value="">All projects</option>
            {projectOptions.map((title) => <option key={title} value={title}>{title}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Status
          <select className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
      </div>

      <div className="mb-4 flex flex-wrap gap-4 text-sm">
        <span className="rounded-lg bg-gray-50 px-3 py-2">Total {filteredRows.length}</span>
        <span className="rounded-lg bg-gray-50 px-3 py-2">Active {filteredRows.filter((r) => !/^cancelled$/i.test(r.status)).length}</span>
        <span className="rounded-lg bg-gray-50 px-3 py-2">Submitted {filteredRows.filter((r) => /^submitted$/i.test(r.status)).length}</span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2 font-medium">Learner</th>
              <th className="px-3 py-2 font-medium">Mobile</th>
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">Started</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Git</th>
              <th className="px-3 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-gray-500">No project enrollments yet.</td></tr>
            )}
            {filteredRows.map((row) => {
              const cancelled = /^cancelled$/i.test(row.status);
              return (
                <tr key={row.enrollmentId} className="border-t align-top">
                  <td className="px-3 py-2">{row.userEmail || row.userProfileId}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{mobiles[row.userProfileId] || '—'}</td>
                  <td className="px-3 py-2">{row.projectTitle}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.dateCreated ? formatDate(row.dateCreated) : '—'}</td>
                  <td className={`px-3 py-2 ${cancelled ? 'text-gray-500' : ''}`}>{row.status}</td>
                  <td className="px-3 py-2">
                    {row.gitRepoUrl
                      ? <a href={row.gitRepoUrl} className="text-primary-700" target="_blank" rel="noreferrer">Repo</a>
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {cancelled ? (
                      <span className="text-xs text-gray-400">Cancelled</span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={cancellingId === row.enrollmentId}
                        onClick={() => void cancelRegistration(row)}
                      >
                        {cancellingId === row.enrollmentId ? 'Cancelling…' : 'Cancel'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectReportsAdminPage;
